import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  HardHat,
  UploadSimple,
  Image,
  Truck,
  Gear,
  ChatCircleDots,
  EnvelopeSimple,
  PaperPlaneTilt,
  FileText,
  ListChecks,
  PencilRuler,
  UserCircle,
  UsersThree,
  Buildings,
  Globe,
  Sparkle,
  FolderSimple,
  MagicWand,
  MagnifyingGlass,
  Stack,
  Storefront,
  Calculator,
  Megaphone,
  Robot,
  Plugs,
  CircleHalf,
  CreditCard,
} from "@phosphor-icons/react";
import { AppHeader } from "@/components/AppHeader";
import { useAuth } from "@/context/AuthContext";

const GROUPS = [
  {
    key: "shop",
    title: "Your shop",
    sub: "Company details, the crew and who can see what",
    icon: Storefront,
    cards: [
      {
        to: "/settings/company",
        icon: Buildings,
        title: "My Company",
        sub: "Name, logo, signature, opening hours, currency and contacts",
        testid: "settings-company",
        admin: true,
      },
      {
        to: "/crew",
        icon: HardHat,
        title: "Crew Accounts",
        sub: "Add crew, set roles and reset passwords",
        testid: "settings-crew",
        admin: true,
      },
      {
        to: "/settings/user-groups",
        icon: UsersThree,
        title: "User Groups",
        sub: "Which pages each group can see and who gets the alerts",
        testid: "settings-user-groups",
        admin: true,
      },
      {
        to: "/account",
        icon: Image,
        title: "My Login & Logo",
        sub: "Upload your logo and change your own password",
        testid: "settings-account",
      },
      {
        to: "/settings/appearance",
        icon: CircleHalf,
        title: "Appearance",
        sub: "Nudge the admin theme darker or lighter — saved in your browser",
        testid: "settings-appearance",
      },
    ],
  },
  {
    key: "stock",
    title: "Stock & suppliers",
    sub: "Getting inventory in and keeping the paperwork together",
    icon: Truck,
    cards: [
      {
        to: "/settings/ai-import",
        icon: MagicWand,
        title: "Import by Supplier Link",
        sub: "Paste a supplier page and let AI fill in the slabs or sinks for you to approve",
        testid: "settings-ai-import",
      },
      {
        to: "/settings/import-slabs",
        icon: UploadSimple,
        title: "Import Slabs",
        sub: "Bulk-add slabs from a CSV of your inventory",
        testid: "settings-import-slabs",
      },
      {
        to: "/settings/import-sinks",
        icon: UploadSimple,
        title: "Import Sinks",
        sub: "Bulk-add sinks from a CSV of your stock",
        testid: "settings-import-sinks",
      },
      {
        to: "/settings/import-prices",
        icon: MagicWand,
        title: "Import & Update Prices",
        sub: "Upload a price list — AI reads your columns and updates slab cost, sale and trade-group prices",
        testid: "settings-import-prices",
      },
      {
        to: "/settings/import-customers",
        icon: UsersThree,
        title: "Import Customers & Contacts",
        sub: "Upload a customer list — AI matches your columns to the right fields for you to approve",
        testid: "settings-import-customers",
      },
      {
        to: "/settings/import-layouts",
        icon: Stack,
        title: "Saved Import Layouts",
        sub: "Rename or remove the supplier column layouts your imports remember",
        testid: "settings-import-layouts",
      },
      {
        to: "/settings/slab-options",
        icon: Stack,
        title: "Slab Materials & Thicknesses",
        sub: "The Material and Thickness pick-lists used when adding slabs and remnants",
        testid: "settings-slab-options",
      },
      {
        to: "/settings/rate-bands",
        icon: Stack,
        title: "Material Rate Bands",
        sub: "Set each material group's expected $/ft² range so the studio flags mispriced drawings",
        testid: "settings-rate-bands",
      },
      {
        to: "/settings/commissions",
        icon: Stack,
        title: "Commissions",
        sub: "When organizations earn commission, monthly statements, applying credit to invoices, quote preview",
        testid: "settings-commissions",
      },
      {
        to: "/inventory/suppliers",
        icon: Truck,
        title: "Suppliers",
        sub: "Contact details and order history",
        testid: "settings-suppliers",
      },
      {
        to: "/settings/media",
        icon: FolderSimple,
        title: "Files & Media",
        sub: "Price lists, care guides, warranties, install photos and video — all in one library",
        testid: "settings-media",
        admin: true,
      },
    ],
  },
  {
    key: "money",
    title: "Quoting & money",
    sub: "How jobs are priced, drawn and billed",
    icon: Calculator,
    cards: [
      {
        to: "/settings/pricing",
        icon: Gear,
        title: "Quote Pricing",
        sub: "Edge profiles, cut-outs, trade discounts and fees",
        testid: "settings-pricing",
      },
      {
        to: "/settings/drawing",
        icon: PencilRuler,
        title: "Drawing",
        sub: "Edge styles, corners, cut-out sizes and printed lines",
        testid: "settings-drawing",
      },
      {
        to: "/settings/quote-stages",
        icon: ListChecks,
        title: "Quote Stages",
        sub: "Add, rename or remove the stages a quote moves through",
        testid: "settings-quote-stages",
      },
      {
        to: "/settings/task-types",
        icon: ListChecks,
        title: "Task Types",
        sub: "Task types for jobs & projects (Templating, Install…), colors and default fields",
        testid: "settings-task-types",
      },
      {
        to: "/settings/schedule-access",
        icon: ListChecks,
        title: "Schedule Access",
        sub: "Choose whether each crew member sees the whole shop schedule or only their own tasks",
        testid: "settings-schedule-access",
      },
      {
        to: "/settings/install-label",
        icon: ListChecks,
        title: "Install Label",
        sub: "Pick what prints on the 4×6 installation label (address, sink, date, QR directions…)",
        testid: "settings-install-label",
      },
      {
        to: "/settings/quote-template",
        icon: FileText,
        title: "Quote Template",
        sub: "Letterhead, notice wording and what the printed quote shows",
        testid: "settings-quote-template",
      },
      {
        to: "/settings/document-templates",
        icon: FileText,
        title: "Document Templates",
        sub: "Per-account Contractor / Retail / Designer wording for quotes, invoices & orders",
        testid: "settings-document-templates",
      },
      {
        to: "/settings/invoicing",
        icon: FileText,
        title: "Invoicing",
        sub: "Deposit %, payment terms and due dates on accepted quotes",
        testid: "settings-invoicing",
      },
      {
        to: "/settings/payments",
        icon: CreditCard,
        title: "Payments",
        sub: "Stripe, QuickBooks Payments and Paya Connect — what customers can pay with",
        testid: "settings-payments",
        admin: true,
      },
    ],
  },
  {
    key: "web",
    title: "Website & customers",
    sub: "What the public and your customers see",
    icon: Globe,
    cards: [
      {
        to: "/settings/website",
        icon: Globe,
        title: "Public Website",
        sub: "Hero, sections, copy, photos, socials, SEO and analytics",
        testid: "settings-website",
        admin: true,
      },
      {
        to: "/settings/portal",
        icon: UserCircle,
        title: "Customer Portal",
        sub: "What customers see: prices, drawings, slab layout, job progress",
        testid: "settings-portal",
      },
    ],
  },
  {
    key: "ai",
    title: "AI helpers",
    sub: "Switches, models and spend caps — off until you turn them on",
    icon: Robot,
    cards: [
      {
        to: "/settings/ai-assistant",
        icon: Sparkle,
        title: "AI Assistant",
        sub: "The shop assistant chat, drafted replies and supplier link imports",
        testid: "settings-ai-assistant",
        admin: true,
      },
      {
        to: "/settings/ai-images",
        icon: Sparkle,
        title: "AI Images",
        sub: "Room mockups for customers, quote renders and website pictures",
        testid: "settings-ai-images",
        admin: true,
      },
    ],
  },
  {
    key: "messaging",
    title: "Email & texting",
    sub: "How messages leave the shop and what they look like",
    icon: Megaphone,
    cards: [
      {
        to: "/settings/email-design",
        icon: EnvelopeSimple,
        title: "Email Design & Notifications",
        sub: "Master on/off switch for all sending, brand every email and switch each automatic message on or off",
        testid: "settings-email-design",
        admin: true,
      },
      {
        to: "/settings/email",
        icon: EnvelopeSimple,
        title: "Email Templates",
        sub: "Edit the wording of customer emails",
        testid: "settings-email",
        admin: true,
      },
      {
        to: "/settings/microsoft-mail",
        icon: PaperPlaneTilt,
        title: "Microsoft 365 Email",
        sub: "Send quotes and invoices from your own Office 365 mailbox",
        testid: "settings-microsoft-mail",
        admin: true,
      },
      {
        to: "/settings/email-activity",
        icon: EnvelopeSimple,
        title: "Email Activity",
        sub: "Every email sent, who it went to and which sender delivered it",
        testid: "settings-email-activity",
      },
      {
        to: "/settings/texting",
        icon: ChatCircleDots,
        title: "Texting (Twilio)",
        sub: "Credentials and reply webhook for customer texts",
        testid: "settings-texting",
        admin: true,
      },
    ],
  },
  {
    key: "integrations",
    title: "Integrations",
    sub: "Connect the tools your shop already uses",
    icon: Plugs,
    cards: [
      {
        to: "/settings/integrations",
        icon: Plugs,
        title: "QuickBooks Online",
        sub: "Sync invoices and paid deposits to your accounting automatically",
        testid: "settings-integrations",
        admin: true,
      },
    ],
  },
];

export default function Settings() {
  const { user } = useAuth();
  const [q, setQ] = useState("");

  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return GROUPS.map((g) => ({
      ...g,
      cards: g.cards.filter(
        (c) =>
          (!c.admin || user?.role === "admin") &&
          (!needle ||
            c.title.toLowerCase().includes(needle) ||
            c.sub.toLowerCase().includes(needle) ||
            g.title.toLowerCase().includes(needle)),
      ),
    })).filter((g) => g.cards.length);
  }, [q, user]);

  const total = groups.reduce((n, g) => n + g.cards.length, 0);

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Settings" />
      <main className="relative z-10 pb-28 pt-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-600">
              Workspace
            </div>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
              Settings
            </h1>
            <p className="mt-1 text-sm text-zinc-500">
              Everything about how the shop runs, grouped so you can find it
            </p>
          </div>
          <div className="relative w-full lg:w-80">
            <MagnifyingGlass
              size={14}
              weight="bold"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600"
            />
            <input
              data-testid="settings-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search settings"
              className="h-11 w-full rounded-lg border border-zinc-800 bg-zinc-900 pl-9 pr-3 text-sm text-zinc-200 outline-none transition-colors duration-150 focus:border-indigo-500"
            />
          </div>
        </div>

        <div className="mt-8 space-y-8" data-testid="settings-cards">
          {groups.map((group) => (
            <section
              key={group.key}
              data-testid={`settings-group-${group.key}`}
            >
              <div className="flex items-center gap-3 border-b border-zinc-800 pb-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-zinc-800 bg-zinc-900 text-zinc-400">
                  <group.icon size={17} weight="bold" />
                </span>
                <div className="min-w-0">
                  <h2 className="text-sm font-bold">{group.title}</h2>
                  <p className="truncate text-xs text-zinc-600">{group.sub}</p>
                </div>
                <span className="font-mono-data ml-auto shrink-0 text-[10px] uppercase tracking-widest text-zinc-700">
                  {group.cards.length}
                </span>
              </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {group.cards.map(({ to, icon: Icon, title, sub, testid }) => (
                  <Link
                    key={to}
                    to={to}
                    data-testid={testid}
                    className="group flex items-start gap-4 rounded-lg border border-zinc-800 bg-zinc-900 p-4 transition-all duration-150 hover:-translate-y-0.5 hover:border-indigo-400/40"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-indigo-400/25 bg-indigo-500/10 text-indigo-300">
                      <Icon size={19} weight="bold" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-bold">{title}</span>
                      <span className="mt-0.5 block text-xs text-zinc-500">
                        {sub}
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </div>

        {!total && (
          <div
            className="mt-6 rounded-lg border border-dashed border-zinc-800 p-10 text-center"
            data-testid="settings-empty"
          >
            <div className="text-sm font-bold text-zinc-300">
              Nothing matches “{q}”
            </div>
            <p className="mt-1 text-xs text-zinc-500">
              Try a word like pricing, email, website or import.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
